"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChunkedReader = void 0;
const CHUNK_SIZE = 256 * 1024;
class ChunkedReader {
    constructor(filelike, chunkSize = CHUNK_SIZE) {
        this._fileCursor = 0;
        this._chunkCursor = 0;
        this._textDecoder = new TextDecoder();
        this._file = filelike;
        this.chunkSize = chunkSize;
    }
    async open() {
        return await this._file.open();
    }
    view() {
        return this._view;
    }
    position() {
        return this._fileCursor - (this._chunk?.byteLength ?? 0) + this._chunkCursor;
    }
    size() {
        return this._file.size();
    }
    remaining() {
        return this.size() - (this._fileCursor - (this._chunk?.byteLength ?? 0) + this._chunkCursor);
    }
    seek(relativeByteOffset) {
        const byteOffset = this.position() + relativeByteOffset;
        if (byteOffset < 0 || byteOffset > this.size()) {
            throw new Error(`Cannot seek to ${byteOffset}`);
        }
        this._fileCursor = byteOffset;
        this._chunkCursor = 0;
        this._chunk = undefined;
        this._view = undefined;
    }
    seekTo(byteOffset) {
        if (byteOffset < 0 || byteOffset > this.size()) {
            throw new Error(`Cannot seek to ${byteOffset}`);
        }
        this._fileCursor = byteOffset;
        this._chunkCursor = 0;
        this._chunk = undefined;
        this._view = undefined;
    }
    async skip(count) {
        const byteOffset = this._chunkCursor + count;
        if (count < 0 || byteOffset < 0 || byteOffset > this.size()) {
            throw new Error(`Cannot skip ${count} bytes`);
        }
        await this.fetch(count);
        this._chunkCursor += count;
    }
    async peekUint8(offset) {
        const view = await this.fetch(offset + 1);
        return view.getUint8(this._chunkCursor + offset);
    }
    async readBytes(count) {
        const view = await this.fetch(count);
        const data = new Uint8Array(view.buffer, view.byteOffset + this._chunkCursor, count);
        this._chunkCursor += count;
        return data;
    }
    async readUint8() {
        const view = await this.fetch(1);
        return view.getUint8(this._chunkCursor++);
    }
    async readInt16() {
        const view = await this.fetch(2);
        const data = view.getInt16(this._chunkCursor, true);
        this._chunkCursor += 2;
        return data;
    }
    async readUint16() {
        const view = await this.fetch(2);
        const data = view.getUint16(this._chunkCursor, true);
        this._chunkCursor += 2;
        return data;
    }
    async readInt32() {
        const view = await this.fetch(4);
        const data = view.getInt32(this._chunkCursor, true);
        this._chunkCursor += 4;
        return data;
    }
    async readUint32() {
        const view = await this.fetch(4);
        const data = view.getUint32(this._chunkCursor, true);
        this._chunkCursor += 4;
        return data;
    }
    async readFloat32() {
        const view = await this.fetch(4);
        const data = view.getFloat32(this._chunkCursor, true);
        this._chunkCursor += 4;
        return data;
    }
    async readFloat64() {
        const view = await this.fetch(8);
        const data = view.getFloat64(this._chunkCursor, true);
        this._chunkCursor += 8;
        return data;
    }
    async readInt64() {
        const view = await this.fetch(8);
        const data = view.getBigInt64(this._chunkCursor, true);
        this._chunkCursor += 8;
        return data;
    }
    async readUint64() {
        const view = await this.fetch(8);
        const data = view.getBigUint64(this._chunkCursor, true);
        this._chunkCursor += 8;
        return data;
    }
    async readString(length) {
        const view = await this.fetch(length);
        const data = this._textDecoder.decode(view.buffer.slice(view.byteOffset + this._chunkCursor, view.byteOffset + this._chunkCursor + length));
        this._chunkCursor += length;
        return data;
    }
    async fetch(bytesRequired) {
        if (bytesRequired > this.remaining()) {
            throw new Error(`Cannot read ${bytesRequired} bytes from ${this.size()} byte source, ${this.remaining()} bytes remaining`);
        }
        if (!this._chunk || this._chunkCursor === this._chunk.byteLength) {
            const fileRemaining = this.size() - this._fileCursor;
            this._chunk = await this._file.read(this._fileCursor, clamp(this.chunkSize, bytesRequired, fileRemaining));
            this._view = new DataView(this._chunk.buffer, this._chunk.byteOffset, this._chunk.byteLength);
            this._chunkCursor = 0;
            this._fileCursor += this._chunk.byteLength;
        }
        let bytesAvailable = this._chunk.byteLength - this._chunkCursor;
        const bytesNeeded = bytesRequired - bytesAvailable;
        if (bytesAvailable < bytesRequired) {
            const fileRemaining = this.size() - this._fileCursor;
            const curChunk = this._chunk;
            const nextChunk = await this._file.read(this._fileCursor, clamp(this.chunkSize, bytesNeeded, fileRemaining));
            this._chunk = concat(curChunk.slice(this._chunkCursor), nextChunk);
            this._view = new DataView(this._chunk.buffer, this._chunk.byteOffset, this._chunk.byteLength);
            this._chunkCursor = 0;
            this._fileCursor += nextChunk.byteLength;
            bytesAvailable = this._chunk.byteLength - this._chunkCursor;
            if (bytesAvailable < bytesRequired) {
                throw new Error(`Requested ${bytesRequired} bytes but ${bytesAvailable} bytes available`);
            }
        }
        return this._view;
    }
}
exports.ChunkedReader = ChunkedReader;
function concat(a, b) {
    const c = new Uint8Array(a.byteLength + b.byteLength);
    c.set(a);
    c.set(b, a.byteLength);
    return c;
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
//# sourceMappingURL=ChunkedReader.js.map